import { colors } from "./colors";
import { theme } from "./theme";

/**
 * What a kind of position looks like, wherever the dashboard draws it.
 *
 * The same money is drawn three times on the dashboard — the KPI strips at
 * the top, the Positions ring and its bar view — and each picked its own
 * colour for it: private equity was violet in the ring and orange in Open
 * Investments, cash a hatched grey in the ring and a flat blue in Total
 * Assets. One position, two looks, and the reader left to work out that
 * they are the same thing. So the kinds are coloured here, once, and every
 * view asks.
 *
 * Fixed per kind rather than taken from the ring's rotation, so a kind keeps
 * its colour whichever is largest today:
 *
 *   - Smartfolios lead in the accent blue, as the product's main holding;
 *   - Trading takes the violet held back from the rotation;
 *   - Goal Based the rotation's magenta;
 *   - Private Equity the orange Open Investments already gave it;
 *   - Cash and the brokerage balance stay grey — money not invested.
 *
 * Keyed loosely, since the label is the backend's word: matched case- and
 * space-insensitively so "cash" and "Cash" land the same.
 */
export const PRIVATE_EQUITY_COLOR = "#EB6834";
export const CASH_COLOR = theme.colors.gray[400];
export const BROKERAGE_COLOR = theme.colors.gray[800];
export const TRADING_COLOR = "#4A3AA7";
export const SMARTFOLIOS_COLOR = theme.colors.primary[500];
export const GOAL_BASED_COLOR = "#B83280";

const key = (label: string) =>
  String(label ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const TYPE_COLOR: Record<string, string> = {
  cash: CASH_COLOR,
  brokerage: BROKERAGE_COLOR,
  privateequity: PRIVATE_EQUITY_COLOR,
  trading: TRADING_COLOR,
  smartfolios: SMARTFOLIOS_COLOR,
  goalbased: GOAL_BASED_COLOR,
};

/**
 * The kinds drawn as a hatch rather than a flat fill: the two that are not
 * invested. The hatch says "not invested" on a second channel beside the
 * grey, so it survives greyscale, a colour deficiency and print. Private
 * equity is invested money and is drawn flat, like every other holding.
 */
const HATCHED = new Set(["cash", "brokerage"]);

export const typeColorFor = (label: string): string | undefined =>
  TYPE_COLOR[key(label)];

export const isHatchedType = (label: string) => HATCHED.has(key(label));

/**
 * The ring's hatch, as a CSS background: the colour's own lines, 1.5px on
 * a 3px period at -45°, over the 100 grey — the same drawing as the ring's
 * SVG pattern, so a swatch beside an arc reads as a piece of it.
 */
export const HATCH_GROUND = colors.gray[100];
export const hatchFill = (color: string) =>
  `repeating-linear-gradient(-45deg, ${color} 0 1.5px, ${HATCH_GROUND} 1.5px 3px)`;
